import { useSession } from "@clerk/clerk-react";
import {
    Button,
    Chip,
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownTrigger,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    Input,
    Switch
} from "@heroui/react";
import { API_URL } from "constants/index";
import { useEffect, useState } from "react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { toast } from "react-toastify";
import { Folder, Plus } from "assets/icons";

const RenovationFolderStructure = ({
    folder,
    level = 0,
    renovationLabels,
    refetchFolderStructure,
}: any) => {
    const { session } = useSession();
    const children = folder.children || [];

    const handleRemoveFileLabel = async (folderId: number, labelId: number) => {
        try {
            await fetchWithAuth(
                `${API_URL}/renovation/folders/${folderId}/labels`,
                { 
                    method: "PUT",
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        labelIds: folder.allowedFileLabels.filter((l: any) => l.id !== labelId).map((l: any) => l.id)
                    })
                },
                session
            );
            toast.success("Label successfully removed");
            refetchFolderStructure();
        } catch (error) {
            toast.error("Failed to remove label");
        }
    };

    const handleAddFileLabel = async (folderId: number, labelId: number) => {
        try {
            await fetchWithAuth(
                `${API_URL}/renovation/folders/${folderId}/labels`,
                { 
                    method: "PUT",
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        labelIds: [...folder.allowedFileLabels.map((l: any) => l.id), labelId]
                    })
                },
                session
            );
            toast.success("Label successfully added");
            refetchFolderStructure();
        } catch (error) {
            toast.error("Failed to add label");
        }
    };

    const getAvailableLabels = () => {
        const currentLabelIds = new Set(folder.allowedFileLabels.map((l: any) => l.id));
        return renovationLabels.filter((label: any) => !currentLabelIds.has(label.id));
    };

    const handleContainsProjectPartsToggle = async () => {
        try {
            await fetchWithAuth(
                `${API_URL}/renovation/folders/${folder.id}/toggle-project-parts`,
                {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" }
                },
                session
            );
            refetchFolderStructure();
        } catch (error) {
            console.error("Error toggling project parts:", error);
            toast.error("Failed to update folder");
        }
    };

    return (
        <section className="flex flex-col gap-2.5">
            <section
                className="flex flex-col gap-2"
                style={{ marginLeft: `${level * 20}px` }}
            >
                <header className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                        <Folder className="h-[18px] w-[18px]" />
                        <div className="flex items-center gap-4">
                            {folder.name}
                            <div className="flex items-center gap-2">
                                <Switch
                                    size="sm"
                                    isSelected={folder.containsProjectParts}
                                    onValueChange={handleContainsProjectPartsToggle}
                                >
                                    <span className="text-sm text-neutral-500">
                                        Bevat projectonderdelen
                                    </span>
                                </Switch>
                            </div>
                        </div>
                    </div>
                    <Dropdown>
                        <DropdownTrigger>
                            <Button
                                className="bg-transparant flex h-fit gap-1 rounded-none p-0 text-sm font-medium text-moss-primary"
                                startContent={<Plus className="h-[18px] w-[18px]" />}
                            >
                                Voeg renovatie bestandstype toe
                            </Button>
                        </DropdownTrigger>
                        <DropdownMenu>
                            {getAvailableLabels().map((label: any) => (
                                <DropdownItem
                                    key={label.id}
                                    onClick={() => handleAddFileLabel(folder.id, label.id)}
                                >
                                    {label.label}
                                </DropdownItem>
                            ))}
                        </DropdownMenu>
                    </Dropdown>
                </header>
                <div className="flex flex-wrap gap-1.5">
                    {folder.allowedFileLabels.map((label: any) => (
                        <Chip
                            key={label.id}
                            onClose={() => handleRemoveFileLabel(folder.id, label.id)}
                            classNames={{
                                base: "flex gap-1 h-fit min-w-fit rounded-full border border-neutral-primary px-2 bg-white text-neutral-primary",
                                content: "font-medium p-0",
                            }}
                        >
                            {label.label}
                        </Chip>
                    ))}
                </div>
            </section>
            {children.map((child: any) => (
                <RenovationFolderStructure
                    key={child.id}
                    folder={child}
                    level={level + 1}
                    renovationLabels={renovationLabels}
                    refetchFolderStructure={refetchFolderStructure}
                />
            ))}
        </section>
    );
};

export const RenovationFoldersTab = () => {
    const { session } = useSession();
    const [folders, setFolders] = useState<any[]>([]);
    const [renovationLabels, setRenovationLabels] = useState<any[]>([]);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [newFolder, setNewFolder] = useState({
        name: '',
        description: '',
        parentId: null as number | null
    });

    const fetchFolders = async () => {
        try {
            const response = await fetchWithAuth(
                `${API_URL}/renovation/folders`,
                { method: "GET" },
                session
            );
            const data = await response.json();
            setFolders(data);
        } catch (error) {
            toast.error("Failed to fetch renovation folders");
        }
    };

    const fetchRenovationLabels = async () => {
        try {
            const response = await fetchWithAuth(
                `${API_URL}/file/labels/renovation`,
                { method: "GET" },
                session
            );
            const data = await response.json();
            setRenovationLabels(data);
        } catch (error) {
            toast.error("Failed to fetch renovation labels");
        }
    };

    useEffect(() => {
        fetchFolders();
        fetchRenovationLabels();
    }, []);

    const handleAddFolder = async () => {
        try {
            await fetchWithAuth(
                `${API_URL}/renovation/folders`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(newFolder)
                },
                session
            );
            toast.success("Folder added successfully");
            setIsAddModalOpen(false);
            fetchFolders();
        } catch (error) {
            toast.error("Failed to add folder");
        }
    };

    return (
        <div className="space-y-4">
            <header className="flex items-center justify-between">
                <h3 className="text-lg font-medium">Renovatie mappenstructuur</h3>
                <Button
                    className="bg-moss-primary text-white"
                    onClick={() => setIsAddModalOpen(true)}
                >
                    Map toevoegen
                </Button>
            </header>

            <p className="mb-2">
                <strong>
                    Hier kan je de mappenstructuur voor renovatieprojecten definiëren. 
                    Deze structuur wordt automatisch aangemaakt voor elk nieuw renovatieproject.
                    Door bestandstypes te koppelen aan mappen bepaal je waar documenten automatisch worden opgeslagen.
                </strong>
            </p>

            {folders.map(folder => (
                <RenovationFolderStructure
                    key={folder.id}
                    folder={folder}
                    renovationLabels={renovationLabels}
                    refetchFolderStructure={fetchFolders}
                />
            ))}

            <Modal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)}>
                <ModalContent>
                    <ModalHeader>Map toevoegen</ModalHeader>
                    <ModalBody className="space-y-4">
                        <Input
                            label="Naam"
                            value={newFolder.name}
                            onChange={(e) => setNewFolder(prev => ({ ...prev, name: e.target.value }))}
                        />
                        <Input
                            label="Beschrijving"
                            value={newFolder.description}
                            onChange={(e) => setNewFolder(prev => ({ ...prev, description: e.target.value }))}
                        />
                    </ModalBody>
                    <ModalFooter>
                        <Button color="danger" variant="light" onPress={() => setIsAddModalOpen(false)}>
                            Annuleren
                        </Button>
                        <Button color="primary" onPress={handleAddFolder}>
                            Toevoegen
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </div>
    );
}; 